import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './AdminLayout.css';
import { Layout, Menu, Input, Avatar, Dropdown, Badge, Drawer, Button, Tooltip, App } from 'antd';
import {
  DashboardOutlined,
  ShoppingOutlined,
  PlusCircleOutlined,
  AppstoreOutlined,
  ReadOutlined,
  PictureOutlined,
  SearchOutlined,
  SettingOutlined,
  KeyOutlined,
  UnlockOutlined,
  LogoutOutlined,
  UserOutlined,
  BellOutlined,
  FilterOutlined,
  MenuOutlined,
  PushpinFilled,
  PushpinOutlined
} from '@ant-design/icons';

const { Header, Sider, Content } = Layout;

const MOBILE_BREAKPOINT = 992;
const PIN_STORAGE_KEY = 'domoelec:admin-sider-pinned';

const AdminLayout = ({ children }) => {
  const { message: msg } = App.useApp();
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    const mobile = window.innerWidth < MOBILE_BREAKPOINT;
    if (mobile) return false;
    try {
      const raw = localStorage.getItem(PIN_STORAGE_KEY);
      const pinned = raw === null ? true : JSON.parse(raw);
      return pinned ? false : true;
    } catch {
      return false;
    }
  });
  const [pinned, setPinned] = useState(() => {
    if (typeof window === 'undefined') return true;
    if (window.innerWidth < MOBILE_BREAKPOINT) return false;
    try {
      const raw = localStorage.getItem(PIN_STORAGE_KEY);
      return raw === null ? true : JSON.parse(raw);
    } catch {
      return true;
    }
  });
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth < MOBILE_BREAKPOINT : false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const siderRef = useRef(null);

  const [searchValue, setSearchValue] = useState('');
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Persistance PIN localStorage
  useEffect(() => {
    if (typeof window === 'undefined' || isMobile) return;
    try { localStorage.setItem(PIN_STORAGE_KEY, JSON.stringify(pinned)); } catch { /* ignore */ }
  }, [pinned, isMobile]);

  // Si PINNED et sider réduit : on ré-ouvre (défaut épinglé = ouvert 250)
  useEffect(() => {
    if (!isMobile && pinned) { setCollapsed(false); }
  }, [pinned, isMobile]);

  // 1) Breakpoint responsive : détecte mobile (<992px) / desktop
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      if (mobile) {
        setCollapsed(false);
        setMobileDrawerOpen(false);
      } else {
        setMobileDrawerOpen(false);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 2) FERMETURE AUTO SIDEBAR AU CLIC HORS ZONE (desktop SEULEMENT)
  //    - Desktop : sider étendu + PAS ÉPINGLÉ → clic hors sider = collapse
  //    - Mobile Drawer : AntD Drawer mask + click on mask ferme nativement.
  useEffect(() => {
    if (isMobile) return;
    const handleDocumentClick = (e) => {
      if (!siderRef.current) return;
      if (pinned) return;
      const target = e.target;
      if (target instanceof Node && !siderRef.current.contains(target)) {
        setCollapsed(true);
      }
    };
    setTimeout(() => {
      document.addEventListener('mousedown', handleDocumentClick);
      document.addEventListener('touchstart', handleDocumentClick, { passive: true });
    }, 0);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
      document.removeEventListener('touchstart', handleDocumentClick);
    };
  }, [isMobile, pinned]);

  // 3) Fermeture auto sidebar au CHANGEMENT DE ROUTE (mobile drawer aussi)
  useEffect(() => {
    if (isMobile) setMobileDrawerOpen(false);
    else if (!pinned) setCollapsed(true);
  }, [location.pathname, isMobile, pinned]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSearch = (value) => {
    if (!value.trim()) {
      msg.warning('Veuillez saisir un terme de recherche');
      return;
    }
    navigate(`/admin/products?search=${encodeURIComponent(value.trim())}`);
  };

  const selectedKey = (() => {
    const p = location.pathname;
    if (p.startsWith('/admin/products/new')) return '/admin/products/new';
    if (p.startsWith('/admin/products')) return '/admin/products';
    if (p.startsWith('/admin/categories/new')) return '/admin/categories/new';
    if (p.startsWith('/admin/categories')) return '/admin/categories';
    if (p.startsWith('/admin/blogs/new')) return '/admin/blogs/new';
    if (p.startsWith('/admin/blogs')) return '/admin/blogs';
    if (p.startsWith('/admin/advertisements')) return '/admin/advertisements';
    if (p.startsWith('/admin/filters')) return '/admin/filters';
    if (p.startsWith('/admin/settings')) return '/admin/settings';
    if (p.startsWith('/admin/admin-pin')) return '/admin/admin-pin';
    return '/admin/dashboard';
  })();

  const openKeys = (() => {
    const p = location.pathname;
    const keys = [];
    if (p.startsWith('/admin/products')) keys.push('products');
    if (p.startsWith('/admin/categories')) keys.push('categories');
    if (p.startsWith('/admin/blogs')) keys.push('blogs');
    if (p.startsWith('/admin/settings') || p.startsWith('/admin/admin-pin')) keys.push('settings-group');
    return keys;
  })();

  const menuItems = [
    {
      key: '/admin/dashboard',
      icon: <DashboardOutlined />,
      label: <Link to="/admin/dashboard">Tableau de bord</Link>,
    },
    {
      key: 'products',
      icon: <ShoppingOutlined />,
      label: 'Produits',
      children: [
        {
          key: '/admin/products/new',
          icon: <PlusCircleOutlined />,
          label: <Link to="/admin/products/new">Nouveau produit</Link>,
        },
        {
          key: '/admin/products',
          icon: <ShoppingOutlined />,
          label: <Link to="/admin/products">Liste produits</Link>,
        },
      ],
    },
    {
      key: 'categories',
      icon: <AppstoreOutlined />,
      label: 'Catégories',
      children: [
        {
          key: '/admin/categories/new',
          icon: <PlusCircleOutlined />,
          label: <Link to="/admin/categories/new">Nouvelle catégorie</Link>,
        },
        {
          key: '/admin/categories',
          icon: <AppstoreOutlined />,
          label: <Link to="/admin/categories">Liste catégories</Link>,
        },
      ],
    },
    {
      key: 'blogs',
      icon: <ReadOutlined />,
      label: 'Blog',
      children: [
        {
          key: '/admin/blogs/new',
          icon: <PlusCircleOutlined />,
          label: <Link to="/admin/blogs/new">Nouvel article</Link>,
        },
        {
          key: '/admin/blogs',
          icon: <ReadOutlined />,
          label: <Link to="/admin/blogs">Liste des articles</Link>,
        },
      ],
    },
    {
      key: '/admin/advertisements',
      icon: <PictureOutlined />,
      label: <Link to="/admin/advertisements">Publicités</Link>,
    },
    {
      key: 'settings-group',
      icon: <SettingOutlined />,
      label: 'Paramètres',
      children: [
        {
          key: '/admin/settings',
          icon: <KeyOutlined />,
          label: <Link to="/admin/settings">Paramètres du compte</Link>,
        },
        {
          key: '/admin/admin-pin',
          icon: <UnlockOutlined />,
          label: <Link to="/admin/admin-pin">Code PIN</Link>,
        },
      ],
    },
  ];

  const userInitials = (user?.username || 'A').slice(0, 2).toUpperCase();

  const renderMenu = () => (
    <Menu
      theme="light"
      mode="inline"
      selectedKeys={[selectedKey]}
      defaultOpenKeys={openKeys}
      items={menuItems}
      className="admin-sider__menu"
      style={{ borderInlineEnd: 'none' }}
    />
  );

  const renderBrand = (isCollapsedView = false) => (
    <div className="admin-sider__brand" style={{ padding: isCollapsedView ? '18px 10px' : '18px 18px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
        <Link to="/admin/dashboard" className="admin-sider__logo" style={{ flex: 1, minWidth: 0 }} onClick={() => isMobile && setMobileDrawerOpen(false)}>
          <img src="/images/LOGO.png" alt="DOMOELEC" />
          {!isCollapsedView && (
            <div className="admin-sider__brand-text">
              <span className="admin-sider__brand-name">DOMOELEC</span>
              <span className="admin-sider__brand-sub">Administration</span>
            </div>
          )}
        </Link>
        {!isMobile && !isCollapsedView && (
          <Tooltip title={pinned ? "Débloquer (clic hors sider fermera)" : "Épingler (garder ouvert)"}>
            <Button
              type="text"
              size="small"
              icon={pinned ? <PushpinFilled style={{ color: '#0ea5e9' }} /> : <PushpinOutlined style={{ color: '#64748b' }} />}
              onClick={e => { e.preventDefault(); e.stopPropagation(); setPinned(p => !p); }}
              style={{ padding: '4px 8px', minWidth: 32 }}
              aria-label={pinned ? "Débloquer le sidebar" : "Épingler le sidebar"}
            />
          </Tooltip>
        )}
      </div>
    </div>
  );

  const renderFooter = (isCollapsedView = false) =>
    !isCollapsedView ? (
      <div className="admin-sider__footer">
        <div className="admin-sider__footer-dot" />
        <span>DOMOELEC © {new Date().getFullYear()}</span>
      </div>
    ) : null;

  return (
    <Layout className="admin-shell">
      {/* ════════════ CAS BUREAU (≥992px) : Sider classique + click-hide + PIN ════════════ */}
      {!isMobile && (
        <Sider
          ref={siderRef}
          width={250}
          collapsed={collapsed}
          collapsedWidth={80}
          className="admin-sider"
          trigger={null}
          collapsible
        >
          {renderBrand(collapsed)}
          {renderMenu()}
          {renderFooter(collapsed)}
        </Sider>
      )}

      {/* ════════════ CAS MOBILE (<992px) : Drawer gauche avec MASQUE ════════════
           - Clic sur MASQUE → FERME automatiquement (propriété native AntD Drawer)
           - Clic sur un lien du menu : onLocationChange ferme le Drawer (useEffect au-dessus)
      */}
      {isMobile && (
        <Drawer
          placement="left"
          closable={false}
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          getContainer={'body'}
          size={260}
          maskClosable={true}
          styles={{
            mask: { backgroundColor: 'rgba(15, 23, 42, 0.35)' },
            header: { display: 'none', padding: 0, borderBottom: 'none' },
            body: { padding: 0, overflowY: 'auto' }
          }}
          rootClassName="admin-sider-drawer"
        >
          <div ref={siderRef} style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            {renderBrand(false)}
            {renderMenu()}
            {renderFooter(false)}
          </div>
        </Drawer>
      )}

      <Layout className="admin-body">
        <Header className="admin-header">
          <div className="admin-header__left">
            {/* Bouton burger = OUVRE sidebar (mobile Drawer) ou toggle collapse (desktop) */}
            <button
              type="button"
              className="admin-header__collapse"
              onClick={() => {
                if (isMobile) {
                  setMobileDrawerOpen(true);
                } else {
                  setCollapsed(!collapsed);
                }
              }}
              aria-label="Menu"
            >
              <i className={`fas ${isMobile ? 'fa-bars' : collapsed ? 'fa-bars' : 'fa-angle-left'}`} />
            </button>
            <Input
              prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
              placeholder="Rechercher un produit..."
              allowClear
              className="admin-header__search"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onPressEnter={(e) => handleSearch(e.target.value)}
            />
          </div>

          <div className="admin-header__right">
            {isMobile && (
              <Button
                type="text"
                icon={<MenuOutlined style={{ fontSize: 20 }} />}
                style={{ marginRight: -4 }}
                onClick={() => setMobileDrawerOpen(true)}
                aria-label="Ouvrir menu"
              />
            )}

            <button type="button" className="admin-header__icon-btn" aria-label="Notifications">
              <Badge dot color="#10b981">
                <BellOutlined style={{ fontSize: 18 }} />
              </Badge>
            </button>

            <Dropdown
              placement="bottomRight"
              trigger={['click']}
              menu={{
                items: [
                  {
                    key: 'profile',
                    icon: <UserOutlined />,
                    label: (
                      <div style={{ padding: '4px 0' }}>
                        <div style={{ fontWeight: 600 }}>{user?.username}</div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>
                          {user?.role === 'admin' ? 'Administrateur' : 'Éditeur'}
                        </div>
                      </div>
                    ),
                    disabled: true,
                  },
                  { type: 'divider' },
                  {
                    key: 'settings',
                    icon: <KeyOutlined />,
                    label: 'Changer le mot de passe',
                    onClick: () => navigate('/admin/settings'),
                  },
                  {
                    key: 'pin',
                    icon: <UnlockOutlined />,
                    label: 'Code PIN',
                    onClick: () => navigate('/admin/admin-pin'),
                  },
                  { type: 'divider' },
                  {
                    key: 'logout',
                    icon: <LogoutOutlined />,
                    label: 'Déconnexion',
                    danger: true,
                    onClick: handleLogout,
                  },
                ],
              }}
            >
              <div className="admin-header__user">
                <Avatar className="admin-header__avatar">{userInitials}</Avatar>
                <div className="admin-header__user-info" style={{ display: isMobile ? 'none' : undefined }}>
                  <span className="admin-header__user-name">{user?.username}</span>
                  <span className="admin-header__user-role">
                    {user?.role === 'admin' ? 'Administrateur' : 'Éditeur'}
                  </span>
                </div>
              </div>
            </Dropdown>
          </div>
        </Header>

        <Content className="admin-content">{children}</Content>
      </Layout>
    </Layout>
  );
};

export default AdminLayout;
